// Results.js - how learners did, simulation by simulation (GET /overview). A simulation's full report - the usual time, the
// common mistakes, each play - is on its Share step in the Studio, where its row leads.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SkeletonLoader from "../common/SkeletonLoader";
import { getOverview } from "../../game-builder/api/gameBuilderAPI";
import { HeadlineStats, SimPicture, StatusChip, ago, pct } from "./overviewParts";
import "./Dashboard.css";

const FILTERS = [{ key: "played", label: "Played" }, { key: "published", label: "Published" }, { key: "all", label: "All" }];

export default function Results() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("played");

  useEffect(() => { getOverview().then(setData).catch((e) => setError(e.message)); }, []);

  const rows = useMemo(() => {
    const sims = data?.simulations || [];
    const kept = filter === "played" ? sims.filter((s) => s.plays > 0) : filter === "published" ? sims.filter((s) => s.status === "published") : sims;
    return [...kept].sort((a, b) => (b.last_played || "").localeCompare(a.last_played || "") || b.plays - a.plays);
  }, [data, filter]);

  if (error) return <p role="alert" style={{ color: "#c0392b" }}>{error}</p>;
  if (!data) return <SkeletonLoader type="dashboard" count={3} />;

  return (
    <div className="db">
      <h1 className="db-title">Results</h1>
      <HeadlineStats data={data} />
      <section className="db-card db-panel">
        <div className="db-panel-head">
          <h2>By simulation</h2>
          <div className="db-filters" role="group" aria-label="Show">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" className={filter === f.key ? "db-filter-on" : ""} aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}>{f.label}</button>
            ))}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="db-empty">
            <p>{filter === "played" ? "No learner has played a simulation yet. Publish one and share its link." : "No simulations here yet."}</p>
          </div>
        ) : (
          <div className="db-table-wrap">
            <table className="db-table">
              <thead>
                <tr><th>Simulation</th><th>Status</th><th>Learners</th><th>Plays</th><th>Completion</th><th>Success</th><th>Last played</th><th aria-label="Open" /></tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={`${s.plan_id}/${s.mission_id}`} onClick={() => navigate(`/app/studio?plan=${encodeURIComponent(s.plan_id)}`)}>
                    <td>
                      <div className="db-row-sim">
                        <SimPicture src={s.picture} className="db-pic-sm" />
                        <div><strong>{s.title}</strong><div className="db-muted">{s.plan}</div></div>
                      </div>
                    </td>
                    <td><StatusChip status={s.status} /></td>
                    <td>{s.learners}</td>
                    <td>{s.plays}</td>
                    <td>{pct(s.completion)}</td>
                    <td>{pct(s.success)}</td>
                    <td className="db-muted">{s.last_played ? ago(s.last_played) : "–"}</td>
                    <td><ChevronRight size={18} aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="db-muted db-note">Completion counts plays that reached an end; success, the finished plays that met the goal.
          Open a simulation and its Share step for the common mistakes and each play.</p>
      </section>
    </div>
  );
}
